import React, { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";

import apiService from "../services/api";
import { activateOnEnterOrSpace } from "../utils/keyboardActivation";
import { getProfileAvatar, getCurrentProfile } from "../utils/profileUtils";
import webPlayerStorage from "../utils/webPlayerStorage";

// Mirrors the Android app's derivePlanDurationLabel(): no expiry → LIFETIME,
// otherwise bucket the created→expire span into a plan label.
const derivePlanDurationLabel = (createdAt, expireDate) => {
  const clean = (s) =>
    String(s || "")
      .split(" - ")[0]
      .trim();
  const expire = clean(expireDate);

  if (!expire || expire === "N/A" || expire.toLowerCase() === "never") {
    return "LIFETIME";
  }

  const created = new Date(clean(createdAt));
  const expires = new Date(expire);

  if (Number.isNaN(created.getTime()) || Number.isNaN(expires.getTime())) {
    return "PREMIUM";
  }

  const days = (expires - created) / (1000 * 60 * 60 * 24);

  if (days < 45) return "MONTHLY";
  if (days < 135) return "QUARTERLY";
  if (days < 260) return "SEMI-ANNUAL";
  if (days < 450) return "YEARLY";

  return "MULTI-YEAR";
};

const UserInfoOverlay = ({ isOpen, onClose, profile, playlist }) => {
  const navigate = useNavigate();
  const [userInfoList, setUserInfoList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userAvatar, setUserAvatar] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Fetch fresh info + premium status when opening
  useEffect(() => {
    if (isOpen) {
      fetchUserInfo();
      setShowLogoutConfirm(false);

      // Check premium status from webPlayerToken
      const webPlayerToken = webPlayerStorage.getToken();

      if (webPlayerToken) {
        try {
          // Decode JWT token (base64 decode the payload)
          const tokenParts = webPlayerToken.split(".");

          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));

            setIsPremium(payload.hasPremium === true);
          }
        } catch (error) {
          console.error("Error decoding token:", error);
        }
      }
    }
  }, [isOpen]);

  // Always fetch fresh avatar when opening
  useEffect(() => {
    if (isOpen) {
      const currentProfile = getCurrentProfile();
      const avatar = getProfileAvatar(currentProfile);

      setUserAvatar(avatar);
    }
  }, [isOpen]);

  // Close on Escape (browser keyboard UX)
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (showLogoutConfirm) setShowLogoutConfirm(false);
        else onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, showLogoutConfirm, onClose]);

  const fetchUserInfo = async () => {
    try {
      setLoading(true);
      const response = await apiService.webPlayerRequest("/v2/user/info");

      if (response?.data) {
        setUserInfoList(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Failed to fetch user info:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarClick = () => {
    // Flag so the overlay reopens when we navigate back from the avatars page
    sessionStorage.setItem("reopenUserInfo", "true");
    navigate("/web-player/avatars", {
      state: { from: "userInfo", profile, playlist },
    });
    onClose();
  };

  // Update avatar when returning from avatar selection
  useEffect(() => {
    const handleFocus = () => {
      const currentProfile = profile || getCurrentProfile();
      const avatar = getProfileAvatar(currentProfile);

      setUserAvatar(avatar);
    };

    window.addEventListener("focus", handleFocus);

    return () => window.removeEventListener("focus", handleFocus);
  }, [profile]);

  // Listen for profile updates from other components
  useEffect(() => {
    const handleProfileUpdate = (event) => {
      const updatedProfile = event.detail?.profile;

      if (updatedProfile) {
        const avatar = getProfileAvatar(updatedProfile);

        setUserAvatar(avatar);
      }
    };

    window.addEventListener("profileUpdated", handleProfileUpdate);

    return () => {
      window.removeEventListener("profileUpdated", handleProfileUpdate);
    };
  }, []);

  const handleSwitchUser = () => {
    navigate("/web-player/profiles");
    onClose();
  };

  const handleSwitchPlaylist = () => {
    navigate("/web-player/switch-playlist");
    onClose();
  };

  const handleLogout = () => {
    webPlayerStorage.clearAll();
    navigate("/web-player/home");
    window.location.reload();
  };

  if (!isOpen) return null;

  // /v2/user/info returns an array (one entry per playlist) — same shape the
  // Android app consumes. Pick the entry for the active playlist.
  const info =
    userInfoList.find((p) => p.playlistName === playlist?.name) ||
    userInfoList[0] ||
    {};

  const expires = info.expireDate || "N/A";
  const tierLabel = derivePlanDurationLabel(info.createdAt, info.expireDate);
  const playlistName =
    info.playlistName || playlist?.name || playlist?.domain || "—";
  const username = info.username || "N/A";
  const profileName = profile?.name || "N/A";
  const statusValue = info.status || "Unknown";
  const connections = `${info.activeConnections ?? 0} / ${
    info.maxConnections ?? 0
  }`;
  const created = info.createdAt || "N/A";

  const details = [
    { icon: "fa-list-ul", label: "Playlist", value: playlistName },
    { icon: "fa-user", label: "Username", value: username },
    { icon: "fa-id-card", label: "Profile", value: profileName },
    { icon: "fa-calendar-alt", label: "Expires", value: expires || "N/A" },
    {
      icon: "fa-link",
      label: "Status",
      value: statusValue,
      valueClass:
        statusValue === "Active" ? "text-green-400" : "text-orange-400",
    },
    { icon: "fa-users", label: "Connections", value: connections },
    { icon: "fa-clock", label: "Created", value: created },
  ];

  const actions = [
    {
      icon: "fa-users",
      title: "Add or switch user",
      subtitle: "Manage profiles for your household",
      onClick: handleSwitchUser,
    },
    {
      icon: "fa-layer-group",
      title: "Manage playlists",
      subtitle: "Add new sources, sort favorites",
      onClick: handleSwitchPlaylist,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-md"
        role="presentation"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative rounded-3xl p-8 max-w-4xl w-full mx-4 bg-gray-900/40 backdrop-blur-2xl border border-white/15 shadow-2xl ring-1 ring-inset ring-white/5">
        {loading ? (
          <div className="flex items-center justify-center h-72">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
          </div>
        ) : (
          <div className="flex gap-8">
            {/* Left Column - Identity */}
            <div className="flex w-[260px] shrink-0 flex-col items-center text-center">
              {/* Premium / Free badge */}
              {isPremium ? (
                <div className="mb-6 flex flex-col items-center">
                  <div className="flex items-center gap-2 rounded-full border border-yellow-500/60 px-5 py-1.5 text-yellow-400">
                    <i className="fas fa-crown" />
                    <span className="text-sm font-semibold tracking-widest">
                      PREMIUM
                    </span>
                  </div>
                  <span className="mt-2 text-xs tracking-[0.3em] text-yellow-500/70">
                    {tierLabel}
                  </span>
                </div>
              ) : (
                <div className="mb-6">
                  <span className="rounded-full border border-gray-600 px-5 py-1.5 text-sm tracking-widest text-gray-400">
                    FREE
                  </span>
                </div>
              )}

              {/* Avatar with gradient ring */}
              <div
                className="group relative cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={handleAvatarClick}
                onKeyDown={activateOnEnterOrSpace}
              >
                <div
                  className={`rounded-full p-[3px] ${
                    isPremium
                      ? "bg-gradient-to-br from-[#6495ED] via-purple-400 to-yellow-400"
                      : "bg-gray-600"
                  }`}
                >
                  <img
                    alt="User Avatar"
                    className="h-52 w-52 rounded-full bg-gray-900 object-cover"
                    src={userAvatar}
                    onError={(e) => {
                      e.target.src = "/default-avatar.png";
                    }}
                  />
                </div>
                <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                  <span className="text-sm font-medium text-white">
                    Change Avatar
                  </span>
                </div>
              </div>

              {/* Log Out */}
              <button
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full border border-gray-600 py-2.5 text-white transition-colors hover:border-red-600 hover:bg-red-600 hover:text-white"
                onClick={() => setShowLogoutConfirm(true)}
              >
                <i className="fas fa-sign-out-alt" />
                <span className="font-medium">Log Out</span>
              </button>

              {/* Name + profile */}
              <div className="mt-6">
                <div className="text-2xl font-bold text-white">{username}</div>
                <div className="mt-1 text-sm text-gray-400">
                  {profileName} · Profile
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="w-px self-stretch bg-white/10" />

            {/* Right Column - Account details */}
            <div className="min-w-0 flex-1">
              <div className="mb-4 text-xs font-semibold tracking-[0.2em] text-gray-400">
                ACCOUNT DETAILS
              </div>

              <div>
                {details.map((row) => (
                  <div
                    className="flex items-center justify-between gap-3 border-b border-white/5 px-3 py-2.5 transition-colors last:border-b-0 hover:bg-white/5"
                    key={row.label}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <i
                        className={`fas ${row.icon} w-4 text-center text-gray-500`}
                      />
                      <span className="text-sm text-gray-300">{row.label}</span>
                    </div>
                    <span
                      className={`truncate text-right text-sm font-medium ${
                        row.valueClass || "text-white"
                      }`}
                    >
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>

              <div className="my-4 border-t border-white/10" />

              <div className="space-y-3">
                {actions.map((action) => (
                  <button
                    className="flex w-full items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.06] p-4 text-left backdrop-blur-md transition-colors hover:bg-white/[0.12]"
                    key={action.title}
                    onClick={action.onClick}
                  >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-blue-500/25 backdrop-blur-sm">
                      <i
                        className={`fas ${action.icon} text-lg text-blue-200`}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-white">
                        {action.title}
                      </div>
                      <div className="truncate text-sm text-gray-400">
                        {action.subtitle}
                      </div>
                    </div>
                    <i className="fas fa-chevron-right text-gray-500" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Logout confirmation */}
        {showLogoutConfirm && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center"
            role="presentation"
            onClick={() => setShowLogoutConfirm(false)}
          >
            <div className="absolute inset-0 rounded-3xl bg-black/50 backdrop-blur-sm" />
            <div
              className="relative mx-4 w-full max-w-sm rounded-2xl border border-white/15 bg-gray-800/80 p-6 text-center backdrop-blur-2xl"
              role="presentation"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="mb-2 text-lg font-bold text-white">Log Out</h3>
              <p className="mb-5 text-sm text-gray-400">
                Are you sure you want to log out?
              </p>
              <div className="flex gap-3">
                <button
                  className="flex-1 rounded-lg bg-gray-700 py-2 text-white transition-colors hover:bg-gray-600"
                  onClick={() => setShowLogoutConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 rounded-lg bg-red-600 py-2 text-white transition-colors hover:bg-red-700"
                  onClick={handleLogout}
                >
                  Yes, Log Out
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserInfoOverlay;
